import React, { useCallback, useMemo } from "react";
import { useAppTranslation } from "locale/useAppTranslation";
import {
  Control,
  Controller,
  useFieldArray,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import PlacesAutocompleteInput from "components/map/PlacesAutocompleteInput";
import Icon from "components/icon";
import {
  DEFAULT_LOCATION,
  DriverTripCalculatorFormData,
  LocationPairRoute,
} from "_interfaces/driverTripCalculator.interfaces";
import { useGoogleMapsDirections } from "../hooks/useGoogleMapsDirections";

interface TripLocationInputProps {
  segmentIndex: number;
  control: Control<DriverTripCalculatorFormData>;
  watch: UseFormWatch<DriverTripCalculatorFormData>;
  setValue: UseFormSetValue<DriverTripCalculatorFormData>;
  onRouteUpdate: (totalDistance: number, totalTime: number, pairs: LocationPairRoute[]) => void;
  pairDistances?: LocationPairRoute[];
}

// Sortable row wrapper
const SortableLocationRow: React.FC<{
  id: string;
  children: React.ReactNode;
}> = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
    position: "relative" as const,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child as React.ReactElement<any>, {
              dragHandleProps: { ...attributes, ...listeners },
            })
          : child,
      )}
    </div>
  );
};

// Single location row
interface LocationRowProps {
  locIndex: number;
  totalLocations: number;
  segmentIndex: number;
  control: Control<DriverTripCalculatorFormData>;
  locationName: string;
  onLocationChange: (locIndex: number, location: { name: string; lat: number; lng: number }) => void;
  onClear: (locIndex: number) => void;
  onRemove: (locIndex: number) => void;
  onAvoidTollChange: (locIndex: number, newValue: boolean) => void;
  dragHandleProps?: Record<string, any>;
  pairDistance?: LocationPairRoute;
}

const LocationRow: React.FC<LocationRowProps> = ({
  locIndex,
  totalLocations,
  segmentIndex,
  control,
  locationName,
  onLocationChange,
  onClear,
  onRemove,
  onAvoidTollChange,
  dragHandleProps,
  pairDistance,
}) => {
  const isStart = locIndex === 0;
  const isEnd = locIndex === totalLocations - 1;
  const isIntermediate = !isStart && !isEnd;
  const hasFilled = locationName && locationName.length > 0;
  const { t } = useAppTranslation("driverTripCalculator");

  const getLocationIcon = () => {
    if (isStart) {
      return (
        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-500">
          <div className="h-2 w-2 rounded-full bg-white" />
        </div>
      );
    }
    if (isEnd) {
      return (
        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500">
          <div className="h-2 w-2 rounded-full bg-white" />
        </div>
      );
    }
    return (
      <div className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-gray-400">
        <div className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      </div>
    );
  };

  const getPlaceholder = () => {
    if (isStart) return t("placeholder-start-point");
    if (isEnd) return t("placeholder-address");
    return t("placeholder-waypoint");
  };

  return (
    <div className="flex items-start gap-2">
      {/* Drag handle */}
      <div
        className="mt-2.5 flex cursor-grab items-center text-gray-400 hover:text-gray-600 active:cursor-grabbing"
        {...dragHandleProps}
      >
        <Icon name="drag" className="h-4 w-4" />
      </div>

      {/* Location icon + connector line */}
      <div className="flex flex-col items-center pt-2.5">
        {getLocationIcon()}
        {locIndex < totalLocations - 1 && (
          <div className="relative mt-1 flex flex-col items-center">
            <div className="h-5 w-0.5 bg-gray-300" />
            {pairDistance && pairDistance.distance_km > 0 && (
              <span className="absolute top-[calc(50%+10px)] -translate-y-1/2 left-3  whitespace-nowrap text-[16px] font-medium leading-none text-gray-800">
                {Math.round(pairDistance.distance_km)} km
              </span>
            )}
          </div>
        )}
      </div>

      {/* Location input */}
      <div className="flex-1">
        <Controller
          name={`segments.${segmentIndex}.locations.${locIndex}.name`}
          control={control}
          render={({ field: formField }) => (
            <PlacesAutocompleteInput
              value={formField.value}
              onChange={(loc) => onLocationChange(locIndex, loc)}
              placeholder={getPlaceholder()}
              data-testid={`input-dtc-location-${segmentIndex + 1}-${locIndex}`}
            />
          )}
        />
      </div>

      {/* Avoid Toll toggle (for non-first locations) */}
      {locIndex > 0 && (
        <div className="mt-1 flex items-center gap-1.5">
          <span className="whitespace-nowrap text-xs text-gray-500">{t("label-avoid-toll")}</span>
          <Controller
            name={`segments.${segmentIndex}.locations.${locIndex}.avoid_toll`}
            control={control}
            render={({ field: tollField }) => (
              <button
                type="button"
                onClick={() => onAvoidTollChange(locIndex, !tollField.value)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  tollField.value ? "bg-primary-500" : "bg-gray-300"
                }`}
                data-testid={`toggle-dtc-avoid-toll-${segmentIndex + 1}-${locIndex}`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    tollField.value ? "translate-x-5" : "translate-x-1"
                  }`}
                />
              </button>
            )}
          />
        </div>
      )}

      {/* Close/delete button */}
      {(isStart || (isEnd && totalLocations <= 2))
        ? (hasFilled && (
            <button
              type="button"
              onClick={() => onClear(locIndex)}
              className="mt-1 rounded-md p-2 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
              title={t("title-clear-location")}
              data-testid={`btn-dtc-clear-location-${segmentIndex + 1}-${locIndex}`}
            >
              <Icon name="close-circle" className="h-4 w-4" />
            </button>
          ))
        : (
          <button
            type="button"
            onClick={() => onRemove(locIndex)}
            className="mt-1 rounded-md p-2 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
            title={t("title-remove-location")}
            data-testid={`btn-dtc-remove-location-${segmentIndex + 1}-${locIndex}`}
          >
            <Icon name="close-circle" className="h-4 w-4" />
          </button>
        )
      }
    </div>
  );
};

const TripLocationInput: React.FC<TripLocationInputProps> = ({
  segmentIndex,
  control,
  watch,
  setValue,
  onRouteUpdate,
  pairDistances,
}) => {
  const { t } = useAppTranslation("driverTripCalculator");
  const { getSegmentRoute } = useGoogleMapsDirections();

  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
  } = useFieldArray({
    control,
    name: `segments.${segmentIndex}.locations`,
  });

  const watchedLocations = watch(`segments.${segmentIndex}.locations`);

  // DnD sensors — require 8px movement before drag starts to avoid conflicts with clicks
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // Stable sortable IDs
  const sortableIds = useMemo(
    () => locationFields.map((f) => f.id),
    [locationFields],
  );

  // Calculate route using current form values
  const calculateRoute = useCallback(
    async (locations?: typeof watchedLocations) => {
      const locs = locations ?? watchedLocations;
      if (!locs || locs.length < 2) return;

      const validLocations = locs.filter(
        (loc) =>
          loc.name &&
          typeof loc.lat === "number" &&
          typeof loc.lng === "number" &&
          loc.lat !== 0 &&
          loc.lng !== 0,
      );

      if (validLocations.length < 2) {
        onRouteUpdate(0, 0, []);
        return;
      }

      const result = await getSegmentRoute(validLocations);
      onRouteUpdate(result.totalDistance, result.totalTime, result.pairs);
    },
    [watchedLocations, getSegmentRoute, onRouteUpdate],
  );

  // Enhancement 1: Trigger on address selection
  const handleLocationChange = useCallback(
    (locIndex: number, location: { name: string; lat: number; lng: number }) => {
      setValue(`segments.${segmentIndex}.locations.${locIndex}.name`, location.name);
      setValue(`segments.${segmentIndex}.locations.${locIndex}.lat`, location.lat);
      setValue(`segments.${segmentIndex}.locations.${locIndex}.lng`, location.lng);

      // Build updated locations array with the new value to avoid stale state
      const updated = watchedLocations.map((loc, i) =>
        i === locIndex ? { ...loc, name: location.name, lat: location.lat, lng: location.lng } : loc,
      );
      calculateRoute(updated);
    },
    [segmentIndex, setValue, watchedLocations, calculateRoute],
  );

  // Enhancement 2: Clear a location (for start/end points)
  const handleClearLocation = useCallback(
    (locIndex: number) => {
      setValue(`segments.${segmentIndex}.locations.${locIndex}`, { ...DEFAULT_LOCATION });

      const updated = watchedLocations.map((loc, i) =>
        i === locIndex ? { ...DEFAULT_LOCATION } : loc,
      );
      calculateRoute(updated);
    },
    [segmentIndex, setValue, watchedLocations, calculateRoute],
  );

  // Enhancement 2: Remove a waypoint (for intermediates)
  const handleRemoveLocation = useCallback(
    (locIndex: number) => {
      if (locationFields.length <= 2) return;
      removeLocation(locIndex);

      const updated = watchedLocations.filter((_, i) => i !== locIndex);
      calculateRoute(updated);
    },
    [locationFields.length, removeLocation, watchedLocations, calculateRoute],
  );

  // Trigger recalculation when avoid toll is toggled
  const handleAvoidTollChange = useCallback(
    (locIndex: number, newValue: boolean) => {
      setValue(`segments.${segmentIndex}.locations.${locIndex}.avoid_toll`, newValue);

      const updated = watchedLocations.map((loc, i) =>
        i === locIndex ? { ...loc, avoid_toll: newValue } : loc,
      );
      calculateRoute(updated);
    },
    [segmentIndex, setValue, watchedLocations, calculateRoute],
  );

  const handleAddLocation = () => {
    appendLocation({ ...DEFAULT_LOCATION });
  };

  // Enhancement 3: Drag-to-reorder
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = sortableIds.indexOf(active.id as string);
      const newIndex = sortableIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      // Build reordered array
      const reordered = [...watchedLocations];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      // Set all location values in new order
      reordered.forEach((loc, i) => {
        setValue(`segments.${segmentIndex}.locations.${i}`, { ...loc });
      });

      calculateRoute(reordered);
    },
    [sortableIds, watchedLocations, segmentIndex, setValue, calculateRoute],
  );

  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-text-primary">
        {t("field-trip-location")} <span className="text-red-500">*</span>
      </label>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {locationFields.map((field, locIndex) => (
              <SortableLocationRow key={field.id} id={field.id}>
                <LocationRow
                  locIndex={locIndex}
                  totalLocations={locationFields.length}
                  segmentIndex={segmentIndex}
                  control={control}
                  locationName={watchedLocations?.[locIndex]?.name || ""}
                  onLocationChange={handleLocationChange}
                  onClear={handleClearLocation}
                  onRemove={handleRemoveLocation}
                  onAvoidTollChange={handleAvoidTollChange}
                  pairDistance={pairDistances?.[locIndex]}
                />
              </SortableLocationRow>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add more location button */}
      <button
        type="button"
        onClick={handleAddLocation}
        className="mt-3 w-full rounded-lg border-2 border-dashed border-primary-300 bg-white px-4 py-2.5 text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors"
        data-testid={`btn-dtc-add-location-${segmentIndex + 1}`}
      >
        {t("btn-add-location")}
      </button>
    </div>
  );
};

export default TripLocationInput;
